import { useEffect, useState } from "react";
import { Table, Badge, Button, ButtonGroup } from "react-bootstrap";

import { getSuspiciousUsers, getBotPatternUsers } from "../utils/Database";
import { LoadingRing } from "../components/LoadingRing";

type SuspiciousUser = {
    user_id: string;
    email: string;
    registered_at: string;
    confirmed: boolean;
    activated: boolean;
    is_blacklisted: boolean;
    secs_to_first_login: number | null;
    downloads_in_window: number;
    previews_in_window: number;
    strikes: number;
    has_long_number: boolean;
    has_repeated_chars: boolean;
    long_local_part: boolean;
    disposable_domain: boolean;
    never_confirmed: boolean;
};

type BotPatternUser = {
    user_id: string;
    email: string;
    is_blacklisted: boolean;
    total_requests: number;
    distinct_files: number;
    repeat_views: number;
    median_gap_seconds: number;
    bursts_under_30s: number;
    pct_bursts_under_30s: number;
    last_request_at: string;
};

const DAY_OPTIONS = [
    { label: "Today", value: 1 },
    { label: "7d", value: 7 },
    { label: "30d", value: 30 },
];

const VIEW_OPTIONS = [
    { label: "Heuristic Flags", value: "heuristic" as const },
    { label: "Bot Pattern", value: "botPattern" as const },
];

function flagCount(u: SuspiciousUser): number {
    return [
        u.has_long_number,
        u.has_repeated_chars,
        u.long_local_part,
        u.disposable_domain,
        u.never_confirmed,
        u.is_blacklisted,
        u.secs_to_first_login !== null && u.secs_to_first_login < 10,
        u.downloads_in_window > 5,
        u.previews_in_window > 20,
        u.strikes > 0,
    ].filter(Boolean).length;
}

export default function AdminSuspicious() {
    const [view, setView] = useState<"heuristic" | "botPattern">("heuristic");

    const [users, setUsers] = useState<SuspiciousUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [daysBack, setDaysBack] = useState(7);

    const [botUsers, setBotUsers] = useState<BotPatternUser[]>([]);
    const [botLoading, setBotLoading] = useState(true);

    async function load(days: number) {
        setLoading(true);
        const data = await getSuspiciousUsers(days);
        setUsers(data);
        setLoading(false);
    }

    async function loadBotUsers() {
        setBotLoading(true);
        const data = await getBotPatternUsers();
        setBotUsers(data);
        setBotLoading(false);
    }

    useEffect(() => { load(daysBack); }, []);
    useEffect(() => { if (view === "botPattern" && botUsers.length === 0) loadBotUsers(); }, [view]);

    return (
        <>
            <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
                <ButtonGroup size="sm">
                    {VIEW_OPTIONS.map((opt) => (
                        <Button
                            key={opt.value}
                            variant={view === opt.value ? "dark" : "outline-secondary"}
                            onClick={() => setView(opt.value)}
                        >
                            {opt.label}
                        </Button>
                    ))}
                </ButtonGroup>

                {view === "heuristic" && (
                    <ButtonGroup size="sm">
                        {DAY_OPTIONS.map((opt) => (
                            <Button
                                key={opt.value}
                                variant={daysBack === opt.value ? "primary" : "outline-secondary"}
                                disabled={loading}
                                onClick={() => {
                                    setDaysBack(opt.value);
                                    load(opt.value);
                                }}
                            >
                                {opt.label}
                            </Button>
                        ))}
                    </ButtonGroup>
                )}

                <span className="text-muted small">
                    {view === "heuristic"
                        ? (loading ? "Loading..." : `${users.length} accounts — flags are signals, not verdicts`)
                        : (botLoading ? "Loading..." : `${botUsers.length} accounts matching coordinated bot pacing`)}
                </span>

                {view === "heuristic" ? (
                    <Button variant="outline-secondary" size="sm" disabled={loading} onClick={() => load(daysBack)}>
                        Refresh
                    </Button>
                ) : (
                    <Button variant="outline-secondary" size="sm" disabled={botLoading} onClick={loadBotUsers}>
                        Refresh
                    </Button>
                )}
            </div>

            {view === "heuristic" ? (
            loading ? (
                <LoadingRing />
            ) : users.length === 0 ? (
                <p className="text-muted">No accounts in this window.</p>
            ) : (
                <div className="table-responsive">
                    <Table striped hover size="sm">
                        <thead>
                            <tr>
                                <th>Email</th>
                                <th className="text-center">Registered</th>
                                <th className="text-center">Status</th>
                                <th className="text-center">Secs to Login</th>
                                <th className="text-center">DLs</th>
                                <th className="text-center">Previews</th>
                                <th className="text-center">Strikes</th>
                                <th>Flags</th>
                                <th className="text-center">#</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((u) => {
                                const fastLogin = u.secs_to_first_login !== null && u.secs_to_first_login < 10;
                                const highDLs = u.downloads_in_window > 5;
                                const highPreviews = u.previews_in_window > 20;
                                const total = flagCount(u);

                                const status = u.is_blacklisted ? "Blacklisted"
                                    : u.never_confirmed ? "Unconfirmed"
                                    : u.activated ? "Active"
                                    : u.confirmed ? "Confirmed"
                                    : "Registered";

                                const statusBg = u.is_blacklisted ? "danger"
                                    : u.never_confirmed ? "danger"
                                    : u.activated ? "success"
                                    : u.confirmed ? "info"
                                    : "warning";

                                return (
                                    <tr key={u.user_id} className={total >= 3 ? "table-danger" : total >= 2 ? "table-warning" : ""}>
                                        <td className="align-middle">{u.email}</td>
                                        <td className="text-center align-middle text-nowrap">
                                            {new Date(u.registered_at).toLocaleDateString()}
                                        </td>
                                        <td className="text-center align-middle">
                                            <Badge bg={statusBg}>{status}</Badge>
                                        </td>
                                        <td className="text-center align-middle">
                                            {u.secs_to_first_login !== null ? (
                                                <span className={fastLogin ? "text-danger fw-bold" : ""}>
                                                    {u.secs_to_first_login}s
                                                </span>
                                            ) : "—"}
                                        </td>
                                        <td className="text-center align-middle">
                                            <span className={highDLs ? "text-danger fw-bold" : ""}>
                                                {u.downloads_in_window}
                                            </span>
                                        </td>
                                        <td className="text-center align-middle">
                                            <span className={highPreviews ? "text-danger fw-bold" : ""}>
                                                {u.previews_in_window}
                                            </span>
                                        </td>
                                        <td className="text-center align-middle">
                                            <span className={u.strikes > 0 ? "text-danger fw-bold" : ""}>
                                                {u.strikes}
                                            </span>
                                        </td>
                                        <td className="align-middle">
                                            <div className="d-flex gap-1 flex-wrap">
                                                {u.disposable_domain   && <Badge bg="danger">disposable</Badge>}
                                                {u.never_confirmed     && <Badge bg="danger">never confirmed</Badge>}
                                                {u.is_blacklisted      && <Badge bg="danger">blacklisted</Badge>}
                                                {fastLogin             && <Badge bg="warning" text="dark">fast login</Badge>}
                                                {u.has_repeated_chars  && <Badge bg="warning" text="dark">repeated chars</Badge>}
                                                {u.has_long_number     && <Badge bg="warning" text="dark">long number</Badge>}
                                                {u.long_local_part     && <Badge bg="warning" text="dark">long address</Badge>}
                                                {highDLs               && <Badge bg="warning" text="dark">high DLs</Badge>}
                                                {highPreviews          && <Badge bg="warning" text="dark">high previews</Badge>}
                                            </div>
                                        </td>
                                        <td className="text-center align-middle">
                                            <Badge
                                                bg={total >= 3 ? "danger" : total >= 2 ? "warning" : total >= 1 ? "secondary" : "light"}
                                                text={total < 3 ? "dark" : undefined}
                                            >
                                                {total}
                                            </Badge>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </div>
            )
            ) : (
            botLoading ? (
                <LoadingRing />
            ) : botUsers.length === 0 ? (
                <p className="text-muted">No accounts currently match the bot pacing signature.</p>
            ) : (
                <div className="table-responsive">
                    <Table striped hover size="sm">
                        <thead>
                            <tr>
                                <th>Email</th>
                                <th className="text-center">Status</th>
                                <th className="text-center">Total Requests</th>
                                <th className="text-center">Distinct Files</th>
                                <th className="text-center">Repeat Views</th>
                                <th className="text-center">Median Gap</th>
                                <th className="text-center">Bursts &lt;30s</th>
                                <th className="text-center">% Bursts &lt;30s</th>
                                <th className="text-center">Last Request</th>
                            </tr>
                        </thead>
                        <tbody>
                            {botUsers.map((u) => (
                                <tr key={u.user_id} className={u.is_blacklisted ? "" : "table-danger"}>
                                    <td className="align-middle">{u.email}</td>
                                    <td className="text-center align-middle">
                                        <Badge bg={u.is_blacklisted ? "secondary" : "danger"}>
                                            {u.is_blacklisted ? "Blacklisted" : "Active"}
                                        </Badge>
                                    </td>
                                    <td className="text-center align-middle">{u.total_requests}</td>
                                    <td className="text-center align-middle">{u.distinct_files}</td>
                                    <td className="text-center align-middle">{u.repeat_views}</td>
                                    <td className="text-center align-middle">{u.median_gap_seconds}s</td>
                                    <td className="text-center align-middle">{u.bursts_under_30s}</td>
                                    <td className="text-center align-middle">
                                        <span className="text-danger fw-bold">{u.pct_bursts_under_30s}%</span>
                                    </td>
                                    <td className="text-center align-middle text-nowrap">
                                        {new Date(u.last_request_at).toLocaleString()}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            )
            )}
        </>
    );
}
